import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AUTO_MS = 6000;

const HeroCarousel = () => {
  const [banners, setBanners]   = useState([]);
  const [i, setI]               = useState(0);
  const [paused, setPaused]     = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef                = useRef(null);

  useEffect(() => {
    api.get('/banners').then((r) => setBanners(r.data)).catch(() => setBanners([]));
  }, []);

  const next = useCallback(() => setI((p) => (banners.length ? (p + 1) % banners.length : 0)), [banners.length]);
  const prev = useCallback(() => setI((p) => (banners.length ? (p - 1 + banners.length) % banners.length : 0)), [banners.length]);

  useEffect(() => {
    if (!banners.length || paused) { setProgress(0); return; }
    setProgress(0);
    const started = Date.now();
    timerRef.current = setInterval(() => {
      const p = Math.min(100, ((Date.now() - started) / AUTO_MS) * 100);
      setProgress(p);
      if (p >= 100) { clearInterval(timerRef.current); next(); }
    }, 80);
    return () => clearInterval(timerRef.current);
  }, [i, banners.length, paused, next]);

  const goTo = (idx) => { setI(idx); setProgress(0); };

  if (!banners.length) return null;

  return (
    <>
      <style>{`
        @keyframes hero-zoom {
          from { transform: scale(1.08); }
          to   { transform: scale(1); }
        }
        .hero-img-active { animation: hero-zoom 8s ease-out forwards; }
      `}</style>

      <section
        className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-6 mt-4 sm:mt-6"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        {/* Banner container */}
        <div
          className="relative overflow-hidden rounded-2xl md:rounded-3xl border border-white/10 shadow-[0_8px_48px_rgba(0,0,0,0.55)] bg-neutral-950"
          style={{ height: 'clamp(200px, 42vw, 480px)' }}
        >
          {/* Slides */}
          {banners.map((b, idx) => (
            <div
              key={b.id || idx}
              aria-hidden={idx !== i}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out"
              style={{ opacity: idx === i ? 1 : 0, zIndex: idx === i ? 10 : 0, pointerEvents: idx === i ? 'auto' : 'none' }}
            >
              {/* Image */}
              {b.image && (
                <img
                  src={b.image}
                  alt={b.title || `Banner ${idx + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover object-center${idx === i ? ' hero-img-active' : ''}`}
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  draggable={false}
                />
              )}

              {/* Bottom gradient — always visible for legibility */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

              {/* Left gradient only when text exists */}
              {(b.title || b.subtitle || b.cta || b.tag) && (
                <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-black/65 to-transparent pointer-events-none" />
              )}

              {/* Text content */}
              {(b.title || b.subtitle || b.cta || b.tag) && (
                <div className="relative z-10 h-full flex items-end sm:items-center">
                  <div className="px-4 sm:px-7 md:px-12 pb-10 sm:pb-0 max-w-2xl">
                    {b.tag && (
                      <span className="inline-flex items-center gap-1.5 bg-orange-500/15 border border-orange-500/40 text-orange-300 text-[10px] sm:text-xs font-bold px-2.5 py-0.5 rounded-full backdrop-blur-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                        {b.tag}
                      </span>
                    )}
                    {b.title && (
                      <h2 className="mt-2 sm:mt-4 text-xl sm:text-3xl md:text-5xl font-black leading-tight text-white drop-shadow-xl">{b.title}</h2>
                    )}
                    {b.subtitle && (
                      <p className="mt-1.5 sm:mt-3 text-xs sm:text-sm md:text-base text-white/80 leading-relaxed max-w-md drop-shadow line-clamp-2">{b.subtitle}</p>
                    )}
                    {(b.cta || b.href) && (
                      <Link to={b.href || '/catalogo'} className="mt-3 sm:mt-5 inline-flex items-center h-9 sm:h-11 px-4 sm:px-6 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-black text-xs sm:text-sm shadow-lg shadow-orange-500/30 hover:scale-[1.03] transition-all duration-200">
                        {b.cta || 'Ver más'} →
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* ── Controls (only when >1 banner) ── */}
          {banners.length > 1 && (
            <>
              {/* Arrows — desktop only */}
              <button
                onClick={prev}
                aria-label="Banner anterior"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 hidden md:flex w-10 h-10 rounded-full items-center justify-center bg-orange-500/20 border border-orange-500/30 text-white hover:bg-orange-500 hover:text-black backdrop-blur-md shadow-lg transition-all duration-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Banner siguiente"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 hidden md:flex w-10 h-10 rounded-full items-center justify-center bg-orange-500/20 border border-orange-500/30 text-white hover:bg-orange-500 hover:text-black backdrop-blur-md shadow-lg transition-all duration-200"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              {/* Dots with progress */}
              <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
                {banners.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goTo(idx)}
                    aria-label={`Ir al banner ${idx + 1}`}
                    className={`relative overflow-hidden rounded-full transition-all duration-300 ${
                      idx === i ? 'w-8 sm:w-10 h-2 bg-white/25' : 'w-2 h-2 bg-white/30 hover:bg-orange-400'
                    }`}
                  >
                    {idx === i && (
                      <span
                        className="absolute inset-y-0 left-0 rounded-full bg-orange-500"
                        style={{ width: `${progress}%`, transition: paused ? 'none' : 'width 80ms linear' }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Slide counter */}
              <div className="absolute top-3 right-3 z-20 bg-black/40 backdrop-blur-sm border border-white/10 text-white/70 text-[10px] sm:text-xs font-bold px-2.5 py-1 rounded-full select-none">
                {i + 1} / {banners.length}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default HeroCarousel;
